/**
 * Tracks how tall the installed app's viewport really is, as a pixel value,
 * for the screens that fill it exactly.
 *
 * The device this was measured on has an 874px screen, and the web view
 * covers all of it. `window.innerHeight`, `visualViewport.height` and CSS
 * `dvh`/`svh` all report 812 — short by exactly the status bar — and `lvh`
 * alone reports 874. Two things say the surface really is the whole screen
 * and the short readings are simply wrong:
 *
 * - `env(safe-area-inset-bottom)` reports the home indicator's 34px, which it
 *   could only do if the web view reached the bottom of the screen; the tab
 *   bar's padding is measurably built from it.
 * - Setting the status bar style to `default`, which would have moved the web
 *   view down out of the bar if the bar were reserving space, changed nothing
 *   on the device.
 *
 * So `measure()` takes the larger of the two families of reading rather than
 * switching sources outright. That also means a device where `lvh` is *not*
 * reliable degrades to the smaller number instead of a new, wrong one, and
 * `screen.height` caps it so it can never exceed the screen itself.
 *
 * This briefly measured the layout viewport instead — `documentElement
 * .clientHeight`, on the reasoning that nothing larger than the viewport can
 * be painted. That reasoning is sound and the premise was wrong: the viewport
 * it asks is the one reporting 812. The shell it sized came up 62px short and
 * the tab bar floated above a band of untouched screen.
 *
 * Only when installed. In a browser tab the viewport legitimately changes as
 * Safari's toolbar slides in and out, and following it would make a pinned
 * footer shuffle up and down the whole time you scroll. That case keeps
 * `100svh`, which is deliberately the height with the toolbar showing.
 */

const isInstalled = () =>
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
  window.matchMedia?.("(display-mode: standalone)").matches === true;

/**
 * An off-screen probe whose only job is to report what `100lvh` resolves to
 * on this device, via layout rather than a CSS variable — so it reflects
 * reality even before anything reads it. Created lazily and reused so
 * `measure()` doesn't grow a new element on every poll.
 */
let lvhProbe: HTMLDivElement | null = null;
const readLvh = () => {
  if (typeof document === "undefined") return 0;
  if (!lvhProbe) {
    lvhProbe = document.createElement("div");
    lvhProbe.setAttribute("aria-hidden", "true");
    lvhProbe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:100lvh;visibility:hidden;pointer-events:none;";
    document.body.appendChild(lvhProbe);
  }
  return lvhProbe.getBoundingClientRect().height;
};

/**
 * The larger of what the engine says is visible now and what `lvh` resolves
 * to, capped at the screen: the app can't be taller than the hardware, and a
 * reading that claims otherwise would hang the tab bar off the bottom.
 * `screen.height` is in portrait-locked CSS pixels here (the manifest pins
 * the orientation), and is ignored if a platform doesn't provide it.
 */
const measure = () => {
  const reported = Math.max(window.visualViewport?.height ?? window.innerHeight, readLvh());
  const screenHeight = window.screen?.height ?? 0;
  return screenHeight > 0 ? Math.min(reported, screenHeight) : reported;
};

/** How often to re-check for the life of the page. Cheap enough to never stop. */
const POLL_MS = 800;

let currentHeight = 0;

/**
 * The store is intentionally a module-level singleton for the app's whole
 * lifetime — `trackAppHeight` only ever runs once, at boot. That's wrong for
 * tests, which call it fresh per case in one shared module instance; this
 * puts it back to a clean slate between them. Not for use outside tests.
 */
export const __resetAppHeightForTests = () => {
  currentHeight = 0;
};

export const trackAppHeight = () => {
  if (typeof window === "undefined" || !isInstalled()) return () => {};

  const apply = () => {
    const height = Math.round(measure());
    if (!height || height === currentHeight) return;
    currentHeight = height;
    document.documentElement.style.setProperty("--app-height", `${height}px`);
  };

  apply();

  const windowEvents = ["resize", "orientationchange", "pageshow"] as const;
  windowEvents.forEach((type) => window.addEventListener(type, apply));
  window.visualViewport?.addEventListener("resize", apply);
  // Coming back from the background is another point where iOS has been seen
  // to hand back a viewport it hasn't finished settling.
  document.addEventListener("visibilitychange", apply);

  // The backstop: keeps this from ever being stuck on a bad one-time reading,
  // regardless of what does or doesn't fire around it. A cold start in
  // particular lands on its final viewport with nothing fired to announce it.
  const poll = setInterval(apply, POLL_MS);

  return () => {
    windowEvents.forEach((type) => window.removeEventListener(type, apply));
    window.visualViewport?.removeEventListener("resize", apply);
    document.removeEventListener("visibilitychange", apply);
    clearInterval(poll);
  };
};

export default trackAppHeight;
